import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function PricingSection() {
  const { pathFor, t } = useLanguage();

  return (
    <section id="pricing" className="bg-[#FAF7F2] border-b border-ct-line py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
          <p className="text-[11px] font-bold uppercase tracking-widest text-ct-brand mb-3">
            {t.pricing.subtitle}
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-ct-ink tracking-tight"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            {t.pricing.title}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:items-stretch">
          {t.pricing.plans.map((plan, i) => {
            const recommended = !!plan.recommended;
            const isEnterprise = plan.name === 'Enterprise' || plan.name === 'Kurumsal';
            return (
              <div
                key={i}
                className={`relative bg-white border rounded-ct-card p-7 flex flex-col transition-all duration-300 ${
                  recommended
                    ? 'border-ct-brand ct-shadow-lg md:-translate-y-2 ring-1 ring-ct-brand/20'
                    : 'border-ct-line ct-shadow-sm hover:-translate-y-0.5 hover:ct-shadow-md'
                }`}
                data-testid={`pricing-plan-${i}`}
              >
                {recommended && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-1 bg-ct-brand text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 ct-shadow-sm">
                    <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                    {t.pricing.recommendedBadge || 'Recommended'}
                  </div>
                )}

                <div className="mb-6">
                  <h3
                    className="font-black text-ct-ink text-lg tracking-tight mb-1"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    {plan.name}
                  </h3>
                  <p className="text-sm text-ct-ink-3 mb-5">{plan.desc}</p>
                  <div className="flex items-baseline gap-1">
                    <span
                      className="text-4xl font-black text-ct-ink tabular-nums"
                      style={{ fontFamily: 'Chivo, sans-serif' }}
                    >
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-ct-ink-3 text-sm font-medium">{plan.period}</span>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-ct-ink-2 leading-relaxed">
                      <span
                        className={`flex-shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center rounded-sm ${
                          recommended ? 'bg-ct-brand text-white' : 'bg-ct-brand/10 text-ct-brand'
                        }`}
                      >
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  to={pathFor(isEnterprise ? '/signin' : '/setup')}
                  data-testid={`pricing-cta-${i}`}
                  className={`text-center font-bold uppercase tracking-wider text-sm py-3 transition-colors duration-200 cursor-pointer ${
                    recommended
                      ? 'bg-ct-brand hover:bg-ct-brand-dark text-white ct-shadow-md'
                      : 'bg-white border border-ct-line-strong hover:border-ct-ink text-ct-ink'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
